import React, { useState } from "react";
import {
  Box,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { ESLintError } from "../LintTriage";
import { FileCard } from "./FileCard";

export interface FileGroup {
  filePath: string;
  errors: ESLintError[];
  eslintCount: number;
  typescriptCount: number;
  totalCount: number;
}

interface FileGroupViewProps {
  files: FileGroup[];
  onViewFile: (filePath: string) => void;
  onErrorsRefresh?: () => void;
}

export const FileGroupView: React.FC<FileGroupViewProps> = ({
  files,
  onViewFile,
  onErrorsRefresh,
}) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [copiedFile, setCopiedFile] = useState("");

  const generateFilePrompt = (file: FileGroup) => {
    const errorList = file.errors
      .map((e, idx) => {
        const errorCode = e.errorCode || e.ruleId;
        return `${idx + 1}. Line ${e.line} - ${errorCode}: ${e.message}`;
      })
      .join("\n");

    return `You are an expert TypeScript developer fixing type errors in a React/TypeScript codebase.

FILE TO FIX: ${file.filePath}

ERRORS TO FIX (${file.totalCount} total):
${errorList}

INSTRUCTIONS:

1. READ THE FILE FIRST
   - Use the Read tool to read the entire file: ${file.filePath}
   - Understand what this file does and its role in the codebase
   - Identify any patterns or conventions used

2. FIX ERRORS METHODICALLY
   - Fix errors one at a time, starting from the top of the file
   - For each error:
     a) Understand the root cause (not just the symptom)
     b) Choose the most type-safe fix (avoid 'any', 'as any', or type assertions unless absolutely necessary)
     c) Prefer proper type definitions over quick fixes
     d) Make minimal changes - don't refactor unrelated code

3. COMMON FIX STRATEGIES
   - Missing properties: Add them to the interface/type definition
   - Type mismatches: Check if the types are correct, fix the source if needed
   - 'any' types: Replace with proper types from the codebase
   - Wrong arguments: Check function signature, pass correct parameters
   - Unsafe conversions: Use proper type guards or restructure the code

4. VERIFICATION (CRITICAL)
   After making fixes:
   - Run: npm run typecheck (must pass with 0 errors in this file)
   - Run: npm run test (if tests exist for this file)
   - Check: No new errors introduced in related files
   - Verify: The actual functionality is unchanged (behavior-preserving fixes only)

5. REPORT YOUR WORK
   When done, report:
   - Total errors fixed: X/${file.totalCount}
   - Any errors you couldn't fix (with explanation)
   - Typecheck status: PASS/FAIL
   - Test status: PASS/FAIL/SKIPPED

IMPORTANT CONSTRAINTS:
- Do NOT use 'as any' or '@ts-ignore' unless absolutely necessary
- Do NOT change functionality - only fix type errors
- Do NOT refactor code beyond what's needed for the fix
- DO maintain existing code style and conventions
- DO read the file first before making any changes

Start by reading ${file.filePath} to understand the code.`;
  };

  const copyFilePrompt = (file: FileGroup) => {
    navigator.clipboard.writeText(generateFilePrompt(file));
    setCopiedFile(file.filePath.split("/").pop() || file.filePath);
    setSnackbarOpen(true);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Files with Errors ({files.length} files)
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom mb={2}>
        Files sorted by error count (most errors first)
      </Typography>

      <Stack spacing={2}>
        {files.map((file) => (
          <FileCard
            key={file.filePath}
            file={file}
            onViewFile={onViewFile}
            onCopy={copyFilePrompt}
            generatePrompt={generateFilePrompt}
            onErrorsRefresh={onErrorsRefresh}
          />
        ))}
      </Stack>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={`Copied all errors from ${copiedFile}`}
      />
    </Box>
  );
};
